import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { MapContainer, TileLayer, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import MarkerClusterGroup from "@/components/MarkerClusterGroup";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { useGuestGate } from "@/components/GuestGate";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useAssistantPageTips } from "@/components/ContextAssistant";
import { Tag, Gift, Recycle, Plus, Search, LocateFixed, X, Bell, BellOff, Bookmark } from "lucide-react";
import { labelCat, labelSub, labelItem } from "@/lib/marketplaceLabels";
import { toast } from "sonner";
import SavedSearchesModal from "@/components/SavedSearchesModal";

export const TYPE_META = {
  sale: { label: "Vendo", color: "#7C3AED", bg: "bg-purple-50", ring: "border-purple-300", icon: Tag },
  gift: { label: "Regalo", color: "#2563EB", bg: "bg-blue-50", ring: "border-blue-300", icon: Gift },
  reusable: { label: "Riutilizzabile trovato", color: "#8B5E34", bg: "bg-amber-50", ring: "border-amber-300", icon: Recycle },
};

// Small helper: re-center the map when user's location is available and proximity is on.
function MapPanTo({ center, enabled }) {
  const map = useMap();
  useEffect(() => {
    if (!enabled || !center) return;
    map.setView(center, Math.max(map.getZoom(), 9), { animate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center?.[0], center?.[1], enabled]);
  return null;
}

export default function MarketplacePage() {
  const nav = useNavigate();
  const loc = useLocation();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const { openGate } = useGuestGate();
  // 14-17 users can browse but cannot publish/contact/follow. Backend enforces
  // this via `_require_adult` — we mirror the rule in the UI so the buttons
  // simply disappear rather than 403-ing.
  const isMinor = !!user && user.is_adult === false;
  useAssistantPageTips([
    { id: "mk-3cats", text: t("tip_mk_3cats") },
    { id: "mk-guest", text: t("tip_mk_guest") },
  ]);
  const [taxonomy, setTaxonomy] = useState({});
  const [type, setType] = useState("all");
  const [category, setCategory] = useState("all");
  const [subcategory, setSubcategory] = useState("all");
  const [itemType, setItemType] = useState("all");
  const [q, setQ] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [country, setCountry] = useState("");
  const [province, setProvince] = useState("");
  // Sort mode for the feed. `distance` is applied client-side after fetch and
  // requires the user to have activated the proximity toggle so we have a
  // reference position; other modes are handled server-side.
  const [sort, setSort] = useState("recent");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // ── Proximity filter (client-side, uses browser geolocation) ──
  const [meLoc, setMeLoc] = useState(null); // [lat, lng]
  const [radiusKm, setRadiusKm] = useState(25);
  const [proxOn, setProxOn] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);

  const askLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocalizzazione non supportata dal browser"); return; }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setMeLoc([p.coords.latitude, p.coords.longitude]); setProxOn(true); setGeoBusy(false); },
      (err) => { setGeoBusy(false); toast.error(err.code === 1 ? "Permesso negato per la posizione" : "Impossibile ottenere la posizione"); },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  };

  // Haversine distance (km)
  const haversineKm = (a, b) => {
    if (!a || !b) return Infinity;
    const R = 6371, toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b[0] - a[0]), dLng = toRad(b[1] - a[1]);
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(s));
  };

  // Items filtered by proximity if enabled, then sorted by user's chosen mode.
  const visibleItems = useMemo(() => {
    let arr = items;
    if (proxOn && meLoc) {
      arr = arr.filter((l) => typeof l.lat === "number" && typeof l.lng === "number" && haversineKm(meLoc, [l.lat, l.lng]) <= radiusKm);
    }
    if (sort === "distance" && meLoc) {
      arr = [...arr].sort((a, b) => {
        const da = typeof a.lat === "number" ? haversineKm(meLoc, [a.lat, a.lng]) : Infinity;
        const db = typeof b.lat === "number" ? haversineKm(meLoc, [b.lat, b.lng]) : Infinity;
        return da - db;
      });
    }
    return arr;
  }, [items, proxOn, meLoc, radiusKm, sort]);

  // Modal state for "Le mie ricerche". Also opened when the user lands here
  // with `?saved=1` (from the header dropdown) or when a saved search
  // notification link is clicked with `?saved=<id>`.
  const [savedModalOpen, setSavedModalOpen] = useState(false);

  useEffect(() => { api.get("/marketplace/taxonomy").then((r) => setTaxonomy(r.data || {})).catch(() => {}); }, []);

  // Pre-fill filters from query string (used by SavedSearchesModal → apply()).
  // We only read once per navigation to avoid clobbering user interactions.
  useEffect(() => {
    const sp = new URLSearchParams(loc.search);
    if (sp.has("saved")) setSavedModalOpen(true);
    const tt = sp.get("type"); if (tt) setType(tt);
    const c = sp.get("category"); if (c) setCategory(c);
    const s = sp.get("subcategory"); if (s) setSubcategory(s);
    const it = sp.get("item_type"); if (it) setItemType(it);
    const qq = sp.get("q"); if (qq) setQ(qq);
    const pmi = sp.get("price_min"); if (pmi) setPriceMin(pmi);
    const pma = sp.get("price_max"); if (pma) setPriceMax(pma);
    const co = sp.get("country_code"); if (co) setCountry(co);
    const pr = sp.get("province"); if (pr) setProvince(pr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.search]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (type !== "all") {
        if (type === "sale" && !user) { openGate({ message: t("guest_login_sale") }); setItems([]); setLoading(false); return; }
        params.type = type;
      }
      if (category !== "all") params.category = category;
      if (subcategory !== "all") params.subcategory = subcategory;
      if (itemType !== "all") params.item_type = itemType;
      if (q) params.q = q;
      if (priceMin) params.price_min = Number(priceMin);
      if (priceMax) params.price_max = Number(priceMax);
      if (country) params.country_code = country;
      if (province) params.province = province;
      // Server handles recent/oldest/price_asc/price_desc; distance is client-side.
      if (["oldest", "price_asc", "price_desc"].includes(sort)) params.sort = sort;
      const { data } = await api.get("/marketplace/listings", { params });
      setItems(data || []);
    } catch (e) {
      if (e.response?.status === 401) { openGate({ message: t("guest_login_sale") }); }
      setItems([]);
    } finally { setLoading(false); }
  };
  // Any active filter that makes a saved search meaningful (empty defaults
  // aren't worth persisting — they would notify the user of literally every
  // new listing). We do not count sort/prox as "filters".
  const hasAnyFilter = (
    type !== "all" || category !== "all" || subcategory !== "all" || itemType !== "all" ||
    !!q || !!priceMin || !!priceMax || !!country || !!province
  );

  const saveCurrentSearch = async () => {
    if (!user) { openGate({ message: "Accedi per salvare una ricerca." }); return; }
    if (!hasAnyFilter) { toast.info("Aggiungi almeno un filtro (categoria, prezzo, provincia, parola…) prima di salvare."); return; }
    const suggested = q ? q : (category !== "all" ? category : "Ricerca");
    const name = (window.prompt("Nome per questa ricerca (max 80):", suggested) || "").trim();
    if (!name) return;
    const params = {};
    if (type !== "all") params.type = type;
    if (category !== "all") params.category = category;
    if (subcategory !== "all") params.subcategory = subcategory;
    if (itemType !== "all") params.item_type = itemType;
    if (q) params.q = q;
    if (priceMin) params.price_min = Number(priceMin);
    if (priceMax) params.price_max = Number(priceMax);
    if (country) params.country_code = country;
    if (province) params.province = province;
    try {
      await api.post("/marketplace/saved-searches", { name, params });
      toast.success("Ricerca salvata. Ti avviseremo dei nuovi annunci compatibili.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile salvare la ricerca");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, category, subcategory, itemType, priceMin, priceMax, country, province, sort]);

  // Debounced live text search: re-load 300ms after the user stops typing.
  // Keeps result list in sync with the query as the user types, without
  // hammering the API on every keystroke. Also fires an immediate load when
  // `q` is cleared (X button or manual delete) so the full list restores.
  useEffect(() => {
    const tm = setTimeout(() => { load(); }, q ? 300 : 0);
    return () => clearTimeout(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Follow/unfollow directly from list/map without opening the detail page.
  const toggleFollow = async (lid, ev) => {
    if (ev) { ev.preventDefault(); ev.stopPropagation(); }
    if (!user) { openGate({ message: "Accedi per seguire un annuncio." }); return; }
    if (isMinor) { toast.info(t("mk_adult_only_button_tip")); return; }
    try {
      const { data } = await api.post(`/marketplace/listings/${lid}/follow`, {});
      setItems((arr) => arr.map((it) => it.listing_id === lid
        ? { ...it, is_following: data.following, followers_count: Math.max(0, (it.followers_count || 0) + (data.following ? 1 : -1)) }
        : it));
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore";
      toast.error(msg);
    }
  };

  const subOptions = category !== "all" && taxonomy[category] ? Object.keys(taxonomy[category]) : [];
  const itemOptions = subcategory !== "all" && taxonomy[category]?.[subcategory] ? taxonomy[category][subcategory] : [];

  // Cluster palette identical to MapHub: solid brand color when uniform,
  // neutral slate when the cluster mixes different content types.
  const MK_CLUSTER = {
    sale:     { from: "#A78BFA", to: "#5B21B6", shadow: "rgba(91,33,182,.35)" },
    gift:     { from: "#60A5FA", to: "#1E40AF", shadow: "rgba(30,64,175,.35)" },
    reusable: { from: "#C08E5C", to: "#5C3A1E", shadow: "rgba(92,58,30,.35)" },
    mixed:    { from: "#94A3B8", to: "#334155", shadow: "rgba(51,65,85,.35)" },
  };
  const mkClusterPalette = (children) => {
    const types = new Set();
    for (const c of children) { const tt = c?.options?.tcType; if (tt) types.add(tt); }
    if (types.size === 1) return MK_CLUSTER[[...types][0]] || MK_CLUSTER.mixed;
    return MK_CLUSTER.mixed;
  };

  const markerFn = (l) => {
    const m = L.marker([l.lat, l.lng], { icon: new L.DivIcon({
      className: "",
      html: `<div style="width:22px;height:22px;background:${TYPE_META[l.type].color};border:3px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,.3)"></div>`,
      iconSize: [22, 22], iconAnchor: [11, 11],
    }), tcType: l.type });
    m.on("click", () => nav(`/marketplace/${l.listing_id}`));
    const ownFlag = user && user.user_id === l.owner_id;
    const followTxt = (ownFlag || isMinor)
      ? ""
      : `<button type="button" data-mk-follow="${l.listing_id}" style="margin-top:6px;padding:4px 10px;border-radius:999px;font-size:11px;font-weight:600;cursor:pointer;border:1px solid ${l.is_following?'#059669':'#e5e7eb'};background:${l.is_following?'#059669':'#fff'};color:${l.is_following?'#fff':'#334155'}">${l.is_following ? t("mk_popup_following") : t("mk_popup_follow")}</button>`;
    const typeLabel = t(`listing_type_${l.type}`) || l.type;
    m.bindPopup(`<div style="font-size:12px"><b>${(l.title || "").replace(/</g, "&lt;")}</b><br>${typeLabel}${l.type==='sale'?` · €${l.price||0}`:''}<br><a href="/marketplace/${l.listing_id}" style="color:#047857">${t("details_arrow")}</a>${followTxt}</div>`);
    m.on("popupopen", (evt) => {
      const el = evt.popup.getElement()?.querySelector(`button[data-mk-follow="${l.listing_id}"]`);
      if (el) el.onclick = (e) => { e.preventDefault(); toggleFollow(l.listing_id); m.closePopup(); };
    });
    return m;
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="marketplace-page">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-4 md:px-8 pt-6 pb-4 flex items-center justify-between gap-2 flex-wrap">
        <BackButton testid="mk-back" />
        {isMinor ? (
          <Button
            data-testid="mk-new-btn-disabled"
            disabled
            title={t("mk_adult_only_button_tip")}
            className="rounded-full bg-slate-200 text-slate-500 hover:bg-slate-200 cursor-not-allowed"
          >
            <Plus size={14} className="mr-1" /> {t("new_listing")} · {t("mk_new_disabled_minor")}
          </Button>
        ) : (
          <Button data-testid="mk-new-btn" onClick={() => user ? nav("/marketplace/new") : openGate({ message: "Accedi per pubblicare un annuncio." })} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white hover:text-white">
            <Plus size={14} className="mr-1" /> {t("new_listing")}
          </Button>
        )}
      </div>
      <div className="max-w-6xl mx-auto px-4 md:px-8 pb-10">
        <div className="text-xs uppercase tracking-[0.25em] text-slate-600 font-semibold mb-1">Marketplace</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">{t("mk_title_row")}</h1>

        {isMinor && (
          <div
            data-testid="mk-adult-only-banner"
            className="mb-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 text-sm px-4 py-3"
          >
            {t("mk_adult_only_banner")}
          </div>
        )}

        <div className="mb-4 flex gap-2 flex-wrap" role="tablist">
          <button data-testid="mk-tab-all" onClick={() => { setType("all"); setCategory("all"); setPriceMin(""); setPriceMax(""); }} className={`h-9 px-4 rounded-full text-xs font-semibold border ${type==="all"?"bg-slate-900 text-white border-slate-900":"bg-white text-slate-700 border-slate-200"}`}>{t("tab_all")}</button>
          {Object.entries(TYPE_META).map(([k, m]) => {
            const Icon = m.icon;
            return (
              <button
                key={k}
                data-testid={`mk-tab-${k}`}
                onClick={() => { setType(k); if (k !== "sale") { setPriceMin(""); setPriceMax(""); } }}
                className={`h-9 px-4 rounded-full text-xs font-semibold border inline-flex items-center gap-1.5 ${type===k?"text-white":""}`}
                style={{ background: type===k?m.color:'#fff', borderColor: type===k?m.color:'#e5e7eb', color: type===k?'#fff':'#334155' }}
              >
                <Icon size={13} /> {t(`mk_type_${k}`)}
              </button>
            );
          })}
        </div>

        <div className="grid md:grid-cols-6 gap-3 mb-4">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("search")}</label>
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="mk-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                placeholder={t("search_marketplace_placeholder")}
                className="rounded-full pl-9 pr-9 mt-1"
              />
              {q && (
                <button
                  type="button"
                  data-testid="mk-search-clear"
                  onClick={() => setQ("")}
                  aria-label={t("clear_search")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-slate-400 hover:text-slate-700"
                >
                  <X size={13} />
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("category")}</label>
            <Select value={category} onValueChange={(v) => { setCategory(v); setSubcategory("all"); setItemType("all"); }}>
              <SelectTrigger data-testid="mk-cat" className="mt-1 rounded-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("all_categories")}</SelectItem>
                {Object.keys(taxonomy).map((c) => <SelectItem key={c} value={c}>{labelCat(c, lang)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("subcategory")}</label>
            <Select value={subcategory} onValueChange={(v) => { setSubcategory(v); setItemType("all"); }}>
              <SelectTrigger data-testid="mk-sub" className="mt-1 rounded-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("all_subcategories")}</SelectItem>
                {subOptions.map((s) => <SelectItem key={s} value={s}>{labelSub(s, lang)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("type_specific_label")}</label>
            <Select value={itemType} onValueChange={setItemType}>
              <SelectTrigger data-testid="mk-item" className="mt-1 rounded-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("all_types")}</SelectItem>
                {itemOptions.map((i) => <SelectItem key={i} value={i}>{labelItem(i, lang)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {type === "sale" && (
            <div className="grid grid-cols-2 gap-1" data-testid="mk-sale-only-filters">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">€ min</label>
                <Input data-testid="mk-price-min" type="number" min={0} value={priceMin} onChange={(e) => setPriceMin(e.target.value.replace(/[^0-9.]/g, ""))} className="rounded-full mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">€ max</label>
                <Input data-testid="mk-price-max" type="number" min={0} value={priceMax} onChange={(e) => setPriceMax(e.target.value.replace(/[^0-9.]/g, ""))} className="rounded-full mt-1" />
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-1">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("country")}</label>
              <Input data-testid="mk-country" value={country} maxLength={2} onChange={(e) => setCountry(e.target.value.toUpperCase())} placeholder="IT" className="rounded-full mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("province_label")}</label>
              <Input data-testid="mk-province" value={province} onChange={(e) => setProvince(e.target.value)} placeholder="Roma…" className="rounded-full mt-1" />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("sort_by")}</label>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="mk-sort" className="mt-1 rounded-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="recent" data-testid="mk-sort-recent">{t("sort_recent")}</SelectItem>
                <SelectItem value="oldest" data-testid="mk-sort-oldest">{t("sort_oldest")}</SelectItem>
                <SelectItem value="price_asc" data-testid="mk-sort-price-asc">{t("sort_price_asc")}</SelectItem>
                <SelectItem value="price_desc" data-testid="mk-sort-price-desc">{t("sort_price_desc")}</SelectItem>
                <SelectItem value="distance" data-testid="mk-sort-distance" disabled={!meLoc}>
                  {meLoc ? t("near_me") : t("near_me_activate")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mb-4 flex gap-2 flex-wrap items-center" data-testid="mk-proximity-bar">
          <button
            data-testid="mk-prox-toggle"
            onClick={() => { if (!meLoc) askLocation(); else setProxOn((v) => !v); }}
            className={`h-9 px-3 rounded-full text-xs font-semibold border inline-flex items-center gap-1.5 ${proxOn ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-200"}`}
          >
            <LocateFixed size={13} /> {geoBusy ? "Localizzazione…" : proxOn ? `Vicino a me · ${radiusKm} km` : "Vicino a me"}
          </button>
          {proxOn && meLoc && (
            <>
              <Select value={String(radiusKm)} onValueChange={(v) => setRadiusKm(Number(v))}>
                <SelectTrigger data-testid="mk-prox-radius" className="h-9 rounded-full w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 5, 10, 25, 50, 100, 250].map((v) => <SelectItem key={v} value={String(v)}>{v} km</SelectItem>)}
                </SelectContent>
              </Select>
              <button data-testid="mk-prox-clear" onClick={() => { setProxOn(false); }} className="h-9 px-3 rounded-full text-xs font-semibold border bg-white text-slate-500 border-slate-200 inline-flex items-center gap-1">
                <X size={12} /> Rimuovi filtro
              </button>
            </>
          )}
          <div className="ml-auto flex gap-2">
            <button
              data-testid="mk-save-search"
              onClick={saveCurrentSearch}
              disabled={!hasAnyFilter}
              className={`h-9 px-3 rounded-full text-xs font-semibold border inline-flex items-center gap-1.5 ${hasAnyFilter ? "bg-white text-emerald-800 border-emerald-300 hover:bg-emerald-50" : "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"}`}
              title={hasAnyFilter ? "Salva questa ricerca e ricevi una notifica quando compare un nuovo annuncio compatibile" : "Aggiungi almeno un filtro per salvare"}
            >
              <Bookmark size={13} /> Salva ricerca
            </button>
            <button
              data-testid="mk-open-saved-searches"
              onClick={() => setSavedModalOpen(true)}
              className="h-9 px-3 rounded-full text-xs font-semibold border bg-white text-slate-700 border-slate-200 inline-flex items-center gap-1.5 hover:bg-slate-50"
            >
              <Bell size={13} /> Le mie ricerche
            </button>
          </div>
        </div>

        <div className="text-xs text-slate-500 mb-2" data-testid="mk-count">
          {visibleItems.length} annunci {loading ? "(caricamento…)" : ""}
          {proxOn && meLoc && <span className="ml-2 text-emerald-700">(entro {radiusKm} km da te)</span>}
        </div>

        {/* Lotto 4 #13 — Mobile scroll fix.
            Previously the two sections were fixed-height (520px each) inside
            a normal document scroll, which produced the "half map + half list"
            situation on iPhone SE. Now on mobile each section fills the
            viewport (`h-[calc(100dvh-…)]`) inside a scroll-snap container so
            a decisive vertical swipe lands naturally on the next section
            without hiding the map when the user wants to pan/zoom. On tablet+
            we keep the original side-by-side grid layout. */}
        <div
          className="md:grid md:grid-cols-2 md:gap-4 md:overflow-visible md:snap-none overflow-y-auto snap-y snap-proximity"
          style={{ height: "calc(100dvh - 220px)", minHeight: 420 }}
          data-testid="mk-map-list-wrap"
        >
          {/* Lotto 6 #5 — discrete 1px emerald border + rounded corners so the
              user sees clearly where the map ends and the next section begins.
              No heavy shadow, no contrasting background — just a subtle
              boundary. */}
          <div className="bg-white border border-emerald-500/30 rounded-2xl overflow-hidden snap-start md:snap-align-none h-[calc(100dvh-260px)] min-h-[380px] md:h-[520px]" data-testid="mk-map">
            <MapContainer center={meLoc || [42, 12]} zoom={meLoc ? 9 : 5} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OSM" />
              <MapPanTo center={meLoc} enabled={proxOn && !!meLoc} />
              {proxOn && meLoc && (
                <Circle center={meLoc} radius={radiusKm * 1000} pathOptions={{ color: "#059669", weight: 1.5, fillOpacity: 0.06 }} />
              )}
              <MarkerClusterGroup
                reports={visibleItems.filter((l) => typeof l.lat === 'number' && typeof l.lng === 'number').map((l) => ({ ...l, report_id: l.listing_id }))}
                buildMarker={markerFn}
                clusterPalette={mkClusterPalette}
              />
            </MapContainer>
          </div>
          <div className="mt-3 md:mt-0 bg-white border border-slate-200 rounded-2xl p-3 overflow-y-auto snap-start md:snap-align-none max-h-[calc(100dvh-260px)] md:max-h-[520px]" data-testid="mk-list">
            {visibleItems.length === 0 && !loading && (
              <div className="text-sm text-slate-500 italic p-4 text-center" data-testid="mk-empty">
                {q
                  ? `Nessun annuncio corrisponde a "${q}". Prova con parole diverse o rimuovi i filtri.`
                  : "Nessun annuncio corrisponde ai filtri attuali."}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              {visibleItems.map((l) => {
                const Icon = TYPE_META[l.type].icon;
                const isOwn = user && user.user_id === l.owner_id;
                return (
                  <Link key={l.listing_id} to={`/marketplace/${l.listing_id}`} data-testid={`mk-item-${l.listing_id}`} className={`relative block rounded-xl border p-2 hover:bg-slate-50 ${TYPE_META[l.type].ring}`}>
                    {!isOwn && !isMinor && (
                      <button
                        type="button"
                        data-testid={`mk-item-follow-${l.listing_id}`}
                        onClick={(e) => toggleFollow(l.listing_id, e)}
                        title={l.is_following ? "Smetti di seguire" : "Segui"}
                        className={`absolute top-1 right-1 z-10 w-7 h-7 rounded-full flex items-center justify-center border ${l.is_following ? "bg-emerald-700 text-white border-emerald-700" : "bg-white/90 text-slate-600 border-slate-200 hover:text-emerald-700"}`}
                      >
                        {l.is_following ? <BellOff size={12} /> : <Bell size={12} />}
                      </button>
                    )}
                    {l.photos && l.photos[0] && (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${l.photos[0]}`} className="w-full h-24 object-cover rounded-lg mb-1" alt="" />
                    )}
                    <div className="flex items-center gap-1 text-[10px] font-semibold mb-0.5" style={{ color: TYPE_META[l.type].color }}>
                      <Icon size={10} /> {t(`mk_type_${l.type}`)}
                    </div>
                    <div className="text-xs font-semibold text-slate-900 truncate">{l.title}</div>
                    {l.type === "sale" && <div className="text-xs text-purple-700 font-bold">€ {l.price} {l.negotiable && <span className="text-[9px] font-normal text-purple-500">(trattabile)</span>}</div>}
                    <div className="text-[10px] text-slate-500 truncate">{l.province_name || ""}</div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <SavedSearchesModal open={savedModalOpen} onClose={() => setSavedModalOpen(false)} />
    </div>
  );
}
